import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, Clock, MapPin, CheckCircle2, ArrowRight, Sparkles, Plus, Minus } from "lucide-react";

const SERVICES = [
  { v: "normal_wash", l: "Normal Wash", emoji: "👕", d: "Machine wash + dry. 24-48h turnaround." },
  { v: "ironing", l: "Ironing", emoji: "♨️", d: "Steam pressed. Returned in 24h." },
  { v: "dry_clean", l: "Dry Clean", emoji: "✨", d: "Premium dry-clean. 48-72h." },
];
const SLOTS = [
  { v: "morning", l: "8 AM – 11 AM" },
  { v: "afternoon", l: "12 PM – 3 PM" },
  { v: "evening", l: "5 PM – 8 PM" },
];

export default function Laundry() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState("normal_wash");
  const [pricelist, setPricelist] = useState({});
  const [items, setItems] = useState({});
  const [pickupDate, setPickupDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0,10));
  const [pickupSlot, setPickupSlot] = useState("morning");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("cod");
  const [busy, setBusy] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [view, setView] = useState("new"); // new | mine

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { nav("/login", { state: { from: "/laundry" }}); return; }
    api.get("/laundry/pricelist").then(r => setPricelist(r.data));
    fetchMine();
    // eslint-disable-next-line
  }, [user, loading]);

  const fetchMine = async () => {
    try { const { data } = await api.get("/laundry/orders/mine"); setMyOrders(data || []); } catch { /* */ }
  };

  const inc = (k) => setItems(it => ({ ...it, [k]: (it[k] || 0) + 1 }));
  const dec = (k) => {
    const cur = items[k] || 0;
    if (cur > 0) setItems({ ...items, [k]: cur - 1 });
  };

  const prices = pricelist[service] || {};
  const total = Object.entries(items).reduce((s, [k, q]) => s + (prices[k] || 0) * (q || 0), 0);
  const finalTotal = Math.max(total, 50);

  const submit = async (e) => {
    e.preventDefault();
    if (!address.trim()) return toast.error("Please add a pickup address");
    if (!Object.values(items).some(v => v > 0)) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const { data } = await api.post("/laundry/orders", {
        service, items, pickup_date: pickupDate, pickup_slot: pickupSlot,
        pickup_address: address, notes, payment_method: payment,
      });
      toast.success(`Order placed! ID: ${data.id.slice(0,8)}`);
      setItems({}); setAddress(""); setNotes("");
      setView("mine");
      fetchMine();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-[#34C97B] focus:ring-2 focus:ring-[#E8FFF3] outline-none transition text-sm";

  const statusColor = (s) => ({
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    assigned: "bg-blue-100 text-blue-700 border-blue-200",
    picked_up: "bg-indigo-100 text-indigo-700 border-indigo-200",
    washing: "bg-purple-100 text-purple-700 border-purple-200",
    out_for_delivery: "bg-emerald-100 text-emerald-700 border-emerald-200",
    delivered: "bg-emerald-600 text-white border-emerald-700",
    cancelled: "bg-gray-200 text-gray-700 border-gray-300",
  })[s] || "bg-gray-100 text-gray-700 border-gray-200";

  if (loading || !user || typeof user !== "object") return null;

  return (
    <div className="bg-[#FBFAF6] min-h-screen" data-testid="laundry-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8FFF3] via-white to-blue-50/40 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#34C97B] text-white text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={14}/> Hostro Laundry
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl mt-4 tracking-tighter text-[#111111]">
            Wash, iron, dry-clean.<br/>
            <span className="text-[#34C97B]">Pickup at your door.</span>
          </h1>
          <p className="mt-3 text-lg text-gray-600 max-w-2xl">Schedule pickup like Zomato. Track every step. Pay COD, UPI, or wallet.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="flex gap-2 mb-6">
          <button onClick={()=>setView("new")} className={`px-4 py-2 rounded-full text-sm font-bold transition ${view==="new" ? "bg-[#34C97B] text-white" : "bg-white border border-gray-200 text-gray-600"}`} data-testid="tab-new">+ New pickup</button>
          <button onClick={()=>setView("mine")} className={`px-4 py-2 rounded-full text-sm font-bold transition ${view==="mine" ? "bg-[#34C97B] text-white" : "bg-white border border-gray-200 text-gray-600"}`} data-testid="tab-mine">My orders ({myOrders.length})</button>
        </div>

        {view === "new" ? (
          <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="laundry-form">
            <div className="bg-white rounded-3xl border border-gray-200 p-6 md:p-8 space-y-6">
              {/* Service */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Select service</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {SERVICES.map(s => (
                    <button type="button" key={s.v} onClick={()=>{setService(s.v); setItems({});}}
                      className={`p-4 rounded-2xl border text-left transition ${
                        service === s.v ? "border-[#34C97B] bg-[#E8FFF3] shadow-md" : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      data-testid={`service-${s.v}`}>
                      <div className="text-2xl">{s.emoji}</div>
                      <div className="font-display font-black text-lg mt-1 text-[#111111]">{s.l}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{s.d}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Choose items</div>
                <div className="space-y-2">
                  {Object.entries(prices).map(([item, price]) => (
                    <div key={item} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3" data-testid={`item-${item}`}>
                      <div>
                        <div className="font-semibold capitalize text-[#111111]">{item.replace("_", " ")}</div>
                        <div className="text-xs text-gray-500">₹{price} each</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={()=>dec(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 grid place-items-center hover:bg-gray-100"><Minus size={14}/></button>
                        <span className="font-bold w-8 text-center">{items[item] || 0}</span>
                        <button type="button" onClick={()=>inc(item)} className="w-8 h-8 rounded-full bg-[#34C97B] text-white grid place-items-center hover:bg-[#22B36A]" data-testid={`inc-${item}`}><Plus size={14}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pickup info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Pickup date</label>
                  <input type="date" required min={new Date().toISOString().slice(0,10)} value={pickupDate} onChange={e=>setPickupDate(e.target.value)} className={inputCls} data-testid="pickup-date"/>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Time slot</label>
                  <div className="flex gap-1.5">
                    {SLOTS.map(s => (
                      <button type="button" key={s.v} onClick={()=>setPickupSlot(s.v)} className={`flex-1 px-2 py-2.5 rounded-xl text-xs font-bold border transition ${pickupSlot === s.v ? "bg-[#34C97B] text-white border-[#34C97B]" : "bg-white text-gray-600 border-gray-200"}`} data-testid={`slot-${s.v}`}>{s.l}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Pickup address</label>
                  <input required className={inputCls} value={address} onChange={e=>setAddress(e.target.value)} placeholder="Hostel block / room number / landmark" data-testid="pickup-address"/>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Notes (optional)</label>
                  <textarea className={`${inputCls} min-h-[60px]`} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Stains, fragile items, urgent..." data-testid="laundry-notes"/>
                </div>
              </div>

              {/* Payment */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Payment method</div>
                <div className="flex gap-2 flex-wrap">
                  {[{v:"cod",l:"Cash on Delivery"},{v:"upi",l:"UPI"},{v:"wallet",l:"Hostro Wallet"}].map(p => (
                    <button type="button" key={p.v} onClick={()=>setPayment(p.v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${payment === p.v ? "bg-[#34C97B] text-white border-[#34C97B]" : "bg-white text-gray-600 border-gray-200"}`} data-testid={`pay-${p.v}`}>{p.l}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary */}
            <aside className="bg-gradient-to-br from-[#34C97B] to-[#22B36A] rounded-3xl p-6 text-white sticky top-24 h-fit">
              <h3 className="font-display font-black text-xl">Order summary</h3>
              <div className="mt-4 space-y-1.5 text-sm">
                {Object.entries(items).filter(([_,q]) => q > 0).map(([k, q]) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k.replace("_", " ")} × {q}</span>
                    <span>₹{(prices[k] || 0) * q}</span>
                  </div>
                ))}
                {Object.values(items).every(v => !v) && <div className="text-white/70">Add items to see total</div>}
              </div>
              <div className="mt-5 pt-4 border-t border-white/20 flex justify-between items-end">
                <div>
                  <div className="text-xs text-white/70 uppercase tracking-wider">Total</div>
                  {total < 50 && total > 0 && <div className="text-xs text-white/70">(₹50 minimum)</div>}
                </div>
                <div className="font-display font-black text-3xl">₹{finalTotal}</div>
              </div>
              <button disabled={busy} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-white text-[#0E1F18] font-bold py-3 rounded-xl hover:bg-gray-100 disabled:opacity-60 transition" data-testid="laundry-submit">
                {busy ? "Placing..." : <>Schedule pickup <ArrowRight size={16}/></>}
              </button>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/80"><CheckCircle2 size={12}/> Free pickup & delivery</div>
            </aside>
          </form>
        ) : (
          <div className="space-y-3" data-testid="my-orders">
            {myOrders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <ShoppingBag size={42} className="mx-auto text-gray-300"/>
                <h3 className="font-display font-black text-2xl mt-3 text-[#111111]">No laundry orders yet</h3>
                <p className="text-gray-500 mt-2">Schedule your first pickup in 2 minutes.</p>
                <button onClick={()=>setView("new")} className="inline-flex items-center gap-2 mt-5 bg-[#34C97B] hover:bg-[#22B36A] text-white font-semibold px-5 py-3 rounded-xl">+ New pickup</button>
              </div>
            ) : myOrders.map(o => (
              <div key={o.id} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition" data-testid={`order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusColor(o.status)}`}>{o.status.replace(/_/g, " ")}</span>
                      <span className="text-xs text-gray-500">#{o.id.slice(0,8)}</span>
                    </div>
                    <h3 className="font-display font-black text-lg text-[#111111] mt-2 capitalize">{o.service.replace("_"," ")} · {o.quantity} item{o.quantity > 1 ? "s" : ""}</h3>
                    <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={12}/>{o.pickup_date} · {o.pickup_slot}</span>
                      <span className="flex items-center gap-1"><MapPin size={12}/>{(o.pickup_address || "").slice(0,40)}</span>
                    </div>
                    {o.vendor_name && <div className="text-xs text-gray-400 mt-1">Vendor: {o.vendor_name}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-2xl text-[#111111]">₹{o.total}</div>
                    <div className="text-xs text-gray-500">{o.payment_method.toUpperCase()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
